import { useState, useEffect } from "react";
import { Search, ChevronsDown, ChevronsUp, Filter, X } from "lucide-react";
import { track } from "@/lib/analytics";

/**
 * In-report controls: search input, filter chips, collapse/expand all.
 *
 * Pure frontend state. No AI. No API calls.
 *
 * Filters supported:
 *  - all
 *  - high_priority  (severity === "high")
 *  - safety         (factors include "safety" hint OR category match)
 *  - moisture       (category includes water/moisture/drainage)
 *  - negotiation    (issue.negotiation_relevance present)
 *  - saved          (user state saved_for_later)
 *  - reviewed       (user state reviewed_at)
 *  - hidden         (user state hidden_by_user — show hidden when toggled)
 */
const FILTERS = [
    { id: "all", label: "All issues" },
    { id: "high_priority", label: "High priority" },
    { id: "safety", label: "Safety" },
    { id: "moisture", label: "Water / moisture" },
    { id: "negotiation", label: "Negotiation-relevant" },
    { id: "saved", label: "Saved for later" },
    { id: "reviewed", label: "Reviewed" },
    { id: "hidden", label: "Hidden" },
];

export default function ReportControls({
    issues = [],
    stateByIssue = {},
    onChange,
}) {
    const [search, setSearch] = useState("");
    const [active, setActive] = useState("all");
    const [collapsed, setCollapsed] = useState(false);

    // Compute live counts per filter so chips can show numbers.
    const counts = {
        all: issues.length,
        high_priority: issues.filter((i) => i.severity === "high").length,
        safety: issues.filter((i) =>
            (i.category || "").toLowerCase().match(/electric|gas|safety|structural/) ||
            (i.cost_exposure?.complexity_factors_applied || []).some((f) =>
                f.includes("structural") || f.includes("asbestos") || f.includes("lead")
            )
        ).length,
        moisture: issues.filter((i) =>
            (i.category || "").toLowerCase().match(/water|moisture|drainage|basement|mold/)
        ).length,
        negotiation: issues.filter((i) => i.negotiation_relevance).length,
        saved: issues.filter((i) => stateByIssue[i.issue_id]?.saved_for_later).length,
        reviewed: issues.filter((i) => stateByIssue[i.issue_id]?.reviewed_at).length,
        hidden: issues.filter((i) => stateByIssue[i.issue_id]?.hidden_by_user).length,
    };

    useEffect(() => {
        onChange?.({ search, filter: active, collapsed });
    }, [search, active, collapsed, onChange]);

    // Lightweight PostHog tracking — debounce search a bit so we don't flood.
    useEffect(() => {
        if (!search.trim()) return;
        const t = setTimeout(() => {
            track("report_search_used", { length: search.trim().length });
        }, 600);
        return () => clearTimeout(t);
    }, [search]);

    const handleFilterClick = (id) => {
        setActive(id);
        if (id !== "all") track("report_filter_used", { filter: id });
    };

    const handleCollapseClick = () => {
        setCollapsed(!collapsed);
        track(collapsed ? "report_expand_all" : "report_collapse_all");
    };

    return (
        <div
            className="bl-card p-4 md:p-5 sticky top-2 z-20 backdrop-blur bg-bl-paper/80"
            data-testid="report-controls"
        >
            <div className="flex flex-wrap gap-3 items-center">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-bl-muted" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search this report — roof, foundation, HVAC…"
                        className="w-full pl-9 pr-8 py-2 rounded-xl bg-bl-surface border border-bl text-sm text-bl placeholder:text-bl-muted focus:outline-none focus:border-bl-primary"
                        data-testid="report-search-input"
                    />
                    {search && (
                        <button
                            type="button"
                            onClick={() => setSearch("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-bl-muted hover:text-bl"
                            aria-label="Clear search"
                            data-testid="report-search-clear"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>

                {/* Collapse / Expand */}
                <button
                    type="button"
                    onClick={handleCollapseClick}
                    className="bl-btn-ghost text-xs"
                    data-testid="report-collapse-toggle"
                >
                    {collapsed ? (
                        <>
                            <ChevronsDown className="w-3.5 h-3.5" /> Expand all
                        </>
                    ) : (
                        <>
                            <ChevronsUp className="w-3.5 h-3.5" /> Collapse all
                        </>
                    )}
                </button>
            </div>

            {/* Filter chips */}
            <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1">
                <Filter className="w-3.5 h-3.5 text-bl-muted flex-shrink-0" />
                {FILTERS.map((f) => {
                    const isActive = active === f.id;
                    const count = counts[f.id];
                    if (count === 0 && f.id !== "all") return null;
                    return (
                        <button
                            key={f.id}
                            type="button"
                            onClick={() => handleFilterClick(f.id)}
                            className={
                                "flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition " +
                                (isActive
                                    ? "bg-bl-primary text-white"
                                    : "bg-bl-surface text-bl-muted border border-bl hover:text-bl")
                            }
                            data-testid={`report-filter-${f.id}`}
                        >
                            {f.label}
                            {typeof count === "number" && (
                                <span className="ml-1.5 opacity-70">{count}</span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Apply current search/filter state to a list of issues.
 */
export function applyReportFilter(issues, stateByIssue, { search = "", filter = "all" } = {}) {
    let out = issues;
    if (filter === "high_priority") {
        out = out.filter((i) => i.severity === "high");
    } else if (filter === "safety") {
        out = out.filter((i) =>
            (i.category || "").toLowerCase().match(/electric|gas|safety|structural/) ||
            (i.cost_exposure?.complexity_factors_applied || []).some((f) =>
                f.includes("structural") || f.includes("asbestos") || f.includes("lead")
            )
        );
    } else if (filter === "moisture") {
        out = out.filter((i) =>
            (i.category || "").toLowerCase().match(/water|moisture|drainage|basement|mold/)
        );
    } else if (filter === "negotiation") {
        out = out.filter((i) => i.negotiation_relevance);
    } else if (filter === "saved") {
        out = out.filter((i) => stateByIssue[i.issue_id]?.saved_for_later);
    } else if (filter === "reviewed") {
        out = out.filter((i) => stateByIssue[i.issue_id]?.reviewed_at);
    } else if (filter === "hidden") {
        out = out.filter((i) => stateByIssue[i.issue_id]?.hidden_by_user);
    } else {
        // "all" → hide user-hidden by default; they show up under the "hidden" filter.
        out = out.filter((i) => !stateByIssue[i.issue_id]?.hidden_by_user);
    }
    const q = (search || "").trim().toLowerCase();
    if (q) {
        out = out.filter((i) => {
            const hay = [
                i.title,
                i.category,
                i.simplified_explanation,
                i.why_it_matters,
                i.recommended_next_step,
                i.specialist,
                ...(i.cost_exposure?.cost_drivers || []),
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return hay.includes(q);
        });
    }
    return out;
}
